import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import {
    Box,
    TextField,
    Checkbox,
    FormControlLabel,
    Typography,
    Link as MuiLink,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import dayjs, { Dayjs } from 'dayjs';
import useBattleGamePopupStyles from '../../styles/kozelBattle/battleGamePopup';
import { useAppDispatch } from '../../stores/store';
import { saveKozelBattleUserDetails } from '../../features/games/kozelBattleSlice';

interface BattleGamePopupProps {
    isOpen: boolean;
    onClose: () => void;
    score: number;
}

const fieldSx = {
    backgroundColor: '#f5e8c9',
    borderRadius: '4px',
    '& .MuiInputBase-input': {
        textAlign: 'center',
        fontWeight: 'bold',
        color: '#4a3824',
        padding: '12px 14px',
    },
    '& input::placeholder': {
        color: '#4a3824',
        textAlign: 'center',
        fontWeight: 'bold',
        opacity: 1,
    },
    '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
    '&:hover .MuiOutlinedInput-notchedOutline': { border: 'none' },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { border: 'none' },
    '& .MuiFormHelperText-root': {
        color: '#ff1744',
        margin: '4px 0 0',
        fontSize: '12px',
        textAlign: 'center',
    },
};

const datePickerSx = {
    ...fieldSx,
    '& .MuiInputLabel-root': {
        color: '#4a3824',
        fontWeight: 'bold',
        left: 0,
        right: 0,
        marginLeft: 'auto',
        marginRight: 'auto',
        width: 'fit-content',
        transform: 'translate(0, 8px) scale(1)',
        '&.MuiInputLabel-shrink': { opacity: 0 },
    },
};

const BattleGamePopup: React.FC<BattleGamePopupProps> = ({ isOpen, onClose, score }) => {
    const dispatch = useAppDispatch();
    const classes = useBattleGamePopupStyles();
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        birthDate: null as Dayjs | null,
        phoneNumber: '',
        agree: false,
    });
    const [errors, setErrors] = useState<{ [key: string]: string }>({});
    const [submitSuccess, setSubmitSuccess] = useState(false);
    const [submitError, setSubmitError] = useState<string | null>(null);
    const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);

    if (!isOpen) return null;

    const validate = () => {
        const newErrors: { [key: string]: string } = {};

        if (!formData.birthDate) newErrors.birthDate = 'Data nașterii este obligatorie';
        if (!formData.lastName) newErrors.lastName = 'Numele este obligatoriu';
        if (!formData.firstName) newErrors.firstName = 'Prenumele este obligatoriu';
        if (!formData.phoneNumber) newErrors.phoneNumber = 'Numărul de telefon este obligatoriu';
        if (!formData.agree) newErrors.agree = 'Trebuie să acceptați regulamentul';

        if (formData.phoneNumber && formData.phoneNumber.length !== 10) {
            newErrors.phoneNumber = 'Numărul de telefon trebuie să fie de 10 cifre';
        }

        if (formData.birthDate) {
            const birth = formData.birthDate.toDate();
            const today = new Date();
            const age = today.getFullYear() - birth.getFullYear();
            const monthDiff = today.getMonth() - birth.getMonth();
            if (
                age < 18 ||
                (age === 18 && monthDiff < 0) ||
                (age === 18 && monthDiff === 0 && today.getDate() < birth.getDate())
            ) {
                newErrors.birthDate = 'Trebuie să aveți cel puțin 18 ani';
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSubmitError(null);

        if (!validate()) {
            return;
        }

        try {
            await dispatch(saveKozelBattleUserDetails({
                firstName: formData.firstName,
                lastName: formData.lastName,
                birthDate: formData.birthDate ? formData.birthDate.format('YYYY-MM-DD') : '',
                phoneNumber: formData.phoneNumber,
            })).unwrap();

            setSubmitSuccess(true);
        } catch (error: any) {
            const message = typeof error === 'string'
                ? error
                : error?.message || 'A apărut o eroare. Vă rugăm să încercați din nou.';
            setSubmitError(message);
        }
    };

    return ReactDOM.createPortal(
        <div className={classes.overlay}>
            <div className={classes.popup}>
                <div className={classes.grid}>

                    {/* ── LEFT – form ── */}
                    <div className={classes.leftColumn}>
                        <Typography className={classes.extraSurprize}>
                            EXTRA SURPRIZE!
                        </Typography>

                        <Typography className={classes.leftSubtitleBold}>
                            Poți câștiga unul din cele 10 hanorace personalizate Kozel!
                        </Typography>

                        <Typography className={classes.leftSubtitle}>
                            Completează formularul de mai jos și înscrie-te la tombolă.
                        </Typography>

                        <Typography className={classes.leftSubtitle}>
                            Poți câștiga, prin tragere la sorți, unul din cele 10 hanorace
                            țapene Kozel!
                        </Typography>

                        {submitSuccess ? (
                            <Typography className={classes.successMessage}>
                                Felicitări! Te-ai înscris cu succes la tombolă.
                            </Typography>
                        ) : (
                            <Box
                                component="form"
                                onSubmit={handleSubmit}
                                sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
                            >
                                <DatePicker
                                    label="Data nașterii*"
                                    value={formData.birthDate}
                                    onAccept={(v) => {
                                        setFormData(prev => ({ ...prev, birthDate: v }));
                                        setIsDatePickerOpen(false);
                                    }}
                                    format="DD/MM/YYYY"
                                    open={isDatePickerOpen}
                                    closeOnSelect={true}
                                    onOpen={() => setIsDatePickerOpen(true)}
                                    onClose={() => setIsDatePickerOpen(false)}
                                    slotProps={{
                                        textField: {
                                            fullWidth: true,
                                            error: !!errors.birthDate,
                                            helperText: errors.birthDate,
                                            onClick: (e) => {
                                                e.preventDefault();
                                                e.stopPropagation();  // ← adaugă stopPropagation
                                                setIsDatePickerOpen(true);
                                            },
                                            sx: datePickerSx,
                                        },
                                        popper: {
                                            sx: { zIndex: 20000 },
                                        },
                                        dialog: {                 
                                            sx: { zIndex: 20000 },
                                        },
                                    }}
                                />

                                <TextField
                                    fullWidth
                                    placeholder="Nume"
                                    value={formData.lastName}
                                    onChange={(e) =>
                                        setFormData(prev => ({ ...prev, lastName: e.target.value }))
                                    }
                                    error={!!errors.lastName}
                                    helperText={errors.lastName}
                                    sx={fieldSx}
                                />

                                <TextField
                                    fullWidth
                                    placeholder="Prenume"
                                    value={formData.firstName}
                                    onChange={(e) =>
                                        setFormData(prev => ({ ...prev, firstName: e.target.value }))
                                    }
                                    error={!!errors.firstName}
                                    helperText={errors.firstName}
                                    sx={fieldSx}
                                />

                                <TextField
                                    fullWidth
                                    placeholder="Număr de telefon"
                                    type="tel"
                                    value={formData.phoneNumber}
                                    onChange={(e) => {
                                        const val = e.target.value.replace(/[^0-9]/g, '');
                                        if (val.length <= 10) {
                                            setFormData(prev => ({ ...prev, phoneNumber: val }));
                                        }
                                    }}
                                    error={!!errors.phoneNumber}
                                    helperText={errors.phoneNumber}
                                    sx={fieldSx}
                                />

                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={formData.agree}
                                            onChange={(e) =>
                                                setFormData(prev => ({ ...prev, agree: e.target.checked }))
                                            }
                                            sx={{
                                                color: '#e9d4a8',
                                                '&.Mui-checked': { color: '#e9d4a8' },
                                            }}
                                        />
                                    }
                                    label={
                                        <Typography sx={{ fontSize: '14px', lineHeight: 1.3, color: '#e9d4a8' }}>
                                            Am citit integral și am înțeles{' '}
                                            <MuiLink
                                                href="/pdf/2026/Regulament Kozel_BLONDE VS DARK_autentificat.pdf"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                sx={{
                                                    color: '#e9d4a8',
                                                    fontStyle: 'italic',
                                                    textDecoration: 'underline',
                                                    fontWeight: 'bold',
                                                    '&:hover': { color: '#ffffff' },
                                                }}
                                            >
                                                Regulamentului Campaniei
                                            </MuiLink>
                                        </Typography>
                                    }
                                    sx={{ '& .MuiFormControlLabel-asterisk': { display: 'none' } }}
                                />

                                {errors.agree && (
                                    <Typography
                                        sx={{ color: '#ff1744', fontSize: '12px', textAlign: 'center', mt: -1 }}
                                    >
                                        {errors.agree}
                                    </Typography>
                                )}

                                {submitError && (
                                    <Typography
                                        sx={{ color: '#ff1744', fontSize: '12px', textAlign: 'center' }}
                                    >
                                        {submitError}
                                    </Typography>
                                )}

                                <button type="submit" className={classes.submitButton}>
                                    MĂ ÎNSCRIU
                                </button>
                            </Box>
                        )}
                    </div>

                    {/* ── RIGHT – result ── */}
                    <div className={classes.rightColumn}>
                        <Typography className={classes.felicitari}>FELICITĂRI!</Typography>

                        <Typography className={classes.rightDescription}>
                            Ai adus multe puncte echipei tale!
                            <br /><br />
                            Deschis la nou, ai ales și Kozel Blonde, și Kozel Dark și ai câștigat extra puncte.
                            {/* <br /><br />
                            Continuă provocarea și înclină balanța punctelor în favoarea echipei
                            tale. */}
                        </Typography>

                        <Typography className={classes.scoreBadge}>
                            {score} pct
                        </Typography>

                        <button className={classes.backButton} onClick={onClose}>
                            ÎNAPOI LA JOC
                        </button>
                    </div>

                </div>
            </div>
        </div>,
        document.body
    );
};

export default BattleGamePopup;